import { useState, useEffect, useCallback } from "react";
import confetti from "canvas-confetti";
import { Trophy, X, Medal, Target, Crown, Zap } from "lucide-react";

const MEDALS = ["text-amber-400", "text-slate-300", "text-orange-400"];
const MEDAL_BG = ["bg-amber-500/20", "bg-slate-400/20", "bg-orange-500/20"];
const MEDAL_LABELS = ["1st", "2nd", "3rd"];

const WinnerRow = ({ rank, name, points, label }) => (
  <div className={`flex items-center gap-3 p-2.5 rounded-lg ${MEDAL_BG[rank]} transition-all`} data-testid={`winner-row-${rank}`}>
    <div className={`w-8 h-8 rounded-full ${MEDAL_BG[rank]} flex items-center justify-center font-bold text-sm ${MEDALS[rank]}`}>
      {MEDAL_LABELS[rank]}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-sm font-medium truncate">{name}</p>
    </div>
    <span className={`text-sm font-bold ${MEDALS[rank]}`}>{points} {label || "pts"}</span>
  </div>
);

const WinnerSection = ({ title, icon: Icon, iconColor, winners, pointLabel }) => {
  if (!winners || winners.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={iconColor} />
        <h4 className="text-sm font-semibold text-slate-300">{title}</h4>
      </div>
      <div className="space-y-1.5">
        {winners.slice(0, 3).map((w, i) => (
          <WinnerRow
            key={i}
            rank={i}
            name={w.team_name || w.display_name || w.name || "User"}
            points={Math.round(w.total_points || w.weekly_points || w.prediction_points || w.cap_prediction_points || 0)}
            label={pointLabel}
          />
        ))}
      </div>
    </div>
  );
};

const CelebrationOverlay = ({ winnersData, pageType, onDismiss }) => {
  const [activeTab, setActiveTab] = useState(pageType || "leaderboard");

  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ["#10b981", "#06b6d4", "#f59e0b", "#a855f7", "#ef4444"];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  useEffect(() => {
    fireConfetti();
  }, [fireConfetti]);

  if (!winnersData) return null;

  const tabs = [
    { key: "leaderboard", label: "Leaderboard", icon: Trophy, color: "text-emerald-400" },
    { key: "matchday", label: "Match Day", icon: Zap, color: "text-amber-400" },
    { key: "predictor", label: "Predictor", icon: Target, color: "text-purple-400" },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="celebration-overlay">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-500/20 via-emerald-500/20 to-purple-500/20 p-5 text-center border-b border-slate-700">
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-700/50 transition-colors"
            data-testid="dismiss-celebration"
          >
            <X className="text-slate-400" size={18} />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-emerald-500 flex items-center justify-center mx-auto mb-2">
            <Crown className="text-white" size={28} />
          </div>
          <h2 className="text-xl font-bold text-white">APL 2026</h2>
          <p className="text-sm text-slate-400 mt-1">Tournament Champions</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-700">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? `${tab.color} border-b-2 border-current bg-slate-700/30`
                  : "text-slate-500 hover:text-slate-300"
              }`}
              data-testid={`celebration-tab-${tab.key}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {activeTab === "leaderboard" && (
            <>
              <WinnerSection
                title="Overall Champions"
                icon={Trophy}
                iconColor="text-emerald-400"
                winners={winnersData.leaderboard?.overall}
                pointLabel="pts"
              />
              <WinnerSection
                title={`Week ${winnersData.leaderboard?.current_week || ''} Top Performers`}
                icon={Medal}
                iconColor="text-cyan-400"
                winners={winnersData.leaderboard?.weekly}
                pointLabel="pts"
              />
            </>
          )}

          {activeTab === "matchday" && (
            <>
              <WinnerSection
                title={`Final Match: ${winnersData.matchday?.match_label || 'Latest'}`}
                icon={Zap}
                iconColor="text-amber-400"
                winners={winnersData.matchday?.latest_match}
                pointLabel="pts"
              />
              <WinnerSection
                title="Cumulative Match Day Champions"
                icon={Crown}
                iconColor="text-orange-400"
                winners={winnersData.matchday?.cumulative}
                pointLabel="pts"
              />
            </>
          )}

          {activeTab === "predictor" && (
            <>
              <WinnerSection
                title="Cap Prediction Champions"
                icon={Target}
                iconColor="text-purple-400"
                winners={winnersData.predictor?.caps}
                pointLabel="pts"
              />
              <WinnerSection
                title="Overall Prediction Champions"
                icon={Medal}
                iconColor="text-cyan-400"
                winners={winnersData.predictor?.overall}
                pointLabel="pts"
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700">
          <button
            onClick={onDismiss}
            className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all text-sm"
            data-testid="celebration-close-btn"
          >
            Continue to APL
          </button>
        </div>
      </div>
    </div>
  );
};

export default CelebrationOverlay;
